import { useState } from 'react'; 
import { toast, Toaster } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Github, Mail, Lock, Eye, EyeOff, User, AlertCircle, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import orbitLogo from '/images/logo.png';
import orbitDarkLogo from '/images/logo-dark.png';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from 'next-themes';
import { MiniFooter } from '@/components/MiniFooter';
import DashboardCarousel from '@/components/landingpage/DashboardCarousel';

interface SignUpProps {
  onSwitchToLogin: () => void;
  onNavigate?: (route: string) => void;
}

export function SignUp({ onSwitchToLogin, onNavigate }: SignUpProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    emailid: '',
    password: '',
    confirmPassword: '',
    profileType: 'individual'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const { signup, githubLogin } = useAuth();
  const validatePassword = () => {
    const errors: string[] = [];

    if (!formData.password) {
      errors.push('Password is required');
    } else {
      if (formData.password.length < 8) {
        errors.push('Password must be at least 8 characters long');
      }
      if (formData.password.length > 30) {
        errors.push('Password must be no more than 30 characters long');
      }
      if (/[^A-Za-z\d@$!%*?&]/.test(formData.password)) {
        errors.push('Password can only contain letters, numbers, and the special characters @$!%*?&');
      }
      if (!/(?=.*[a-z])/.test(formData.password)) {
        errors.push('Password must contain at least one lowercase letter');
      }
      if (!/(?=.*[A-Z])/.test(formData.password)) {
        errors.push('Password must contain at least one uppercase letter');
      }
      if (!/(?=.*\d)/.test(formData.password)) {
        errors.push('Password must contain at least one number');
      }
      if (!/(?=.*[@$!%*?&])/.test(formData.password)) {
        errors.push('Password must contain at least one special character (@$!%*?&)');
      }
    }

    if (!formData.confirmPassword) {
      errors.push('Please confirm your password');
    } else if (formData.password !== formData.confirmPassword) {
      errors.push('Passwords do not match');
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePassword()) {

      return;
    }

    setIsLoading(true);
    try {
      await signup({
        name: formData.name,
        emailid: formData.emailid,
        password: formData.password,
        profileType: formData.profileType
      });


      toast.success('Account created successfully! Please check your email to verify your account.');

      setTimeout(() => {
        onSwitchToLogin();
      }, 3000);

    } catch (error) {

    } finally {
      setIsLoading(false);
    }
  };

  const handleGithubSignup = async () => {
    setIsLoading(true);
    try {
      await githubLogin();
    } catch (error) {
      toast.error('GitHub signup failed. Please try again.');
    }
    setIsLoading(false);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }

  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary to-primary/80 items-center justify-center p-12">
        <div className="w-full max-w-2xl text-center">
          <div className="mb-8">
            <DashboardCarousel
              images={["/images/landing-1.png", "/images/landing-2.png", "/images/landing-3.png"]}
            />
          </div>
          {/* <h2 className="text-3xl font-bold text-white mb-4">
            Join Our Platform
          </h2> */}
          <p className="text-blue-100 text-lg leading-relaxed">
            {/* Start securing your code today with advanced scanning and comprehensive vulnerability management. */}
            Join ORbiT8 and secure your software supply chain with intelligent scanning and enterprise-grade vulnerability management.
          </p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">

          <div className="mb-4">
            <Button
              asChild
              variant="ghost"
              size="icon"
              aria-label="Back to home page"
              title="Back to home"
              className="size-9 text-muted-foreground hover:text-foreground"
            >
              <Link to="/">
                <ArrowLeft className="size-4" />
              </Link>
            </Button>
          </div>

          <div className="text-center mb-8">
            <Link
              to="/"
              aria-label="ORbiT8 home"
              className="inline-block transition-opacity hover:opacity-80"
            >
              <img
                src={theme === 'dark' ? orbitDarkLogo : orbitLogo}
                alt="ORbiT8"
                className="h-12 w-auto mx-auto mb-4"
              />
            </Link>
          </div>

          <Card className="bg-card shadow-lg rounded-lg border">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle className="text-xl text-center">Sign Up</CardTitle>
              <CardDescription className="text-center">
                Create your account to get started
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
           
              <Button
                onClick={handleGithubSignup}
                disabled={isLoading}
                variant="outline"
                className="w-full border-border hover:bg-accent transition-colors"
              >
                <Github className="w-5 h-5 mr-2" />
                Continue with GitHub
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">Or continue with email</span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
              {validationErrors.length > 0 && (
                  <Alert className="border-red-200 bg-red-50">
                    <AlertCircle className="w-4 h-4 text-red-600" />
                    <AlertDescription className="text-red-800">
                      <ul className="list-disc list-inside space-y-1">
                        {validationErrors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4 z-10" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="Enter your full name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="pl-10"
                      maxLength={100}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="emailid">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4 z-10" />
                    <Input
                      id="emailid"
                      type="email"
                      placeholder="Enter your email"
                      value={formData.emailid}
                      onChange={(e) => handleInputChange('emailid', e.target.value)}
                      className="pl-10"
                      maxLength={320}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4 z-10" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      className="pl-10 pr-10"
                      maxLength={128}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Must be 8+ characters with uppercase, lowercase, a number, and a special character (@$!%*?&)
                  </p>

                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4 z-10" />
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm your password"
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      className="pl-10 pr-10"
                      maxLength={128}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    id="terms"
                    type="checkbox"
                    className="rounded border-border text-primary focus:ring-primary/20"
                    required
                  />
                  <Label htmlFor="terms" className="text-sm text-muted-foreground">
                    I agree to the{' '}
                    <button
                      type="button"
                      onClick={() => onNavigate?.('terms-of-service')}
                      className="text-primary hover:text-primary/80 hover:underline bg-transparent border-none p-0 cursor-pointer"
                    >
                      Terms of Service
                    </button>
                    {' '}and{' '}
                    <button
                      type="button"
                      onClick={() => onNavigate?.('privacy-policy')}
                      className="text-primary hover:text-primary/80 hover:underline bg-transparent border-none p-0 cursor-pointer"
                    >
                      Privacy Policy
                    </button>
                  </Label>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full shadow-lg shadow-primary/20"
                >
                  {isLoading ? "Creating account..." : "Create Account"}
                </Button>
              </form>

              <div className="text-center">
                <span className="text-muted-foreground">Already have an account? </span>
                <Button
                  variant="link"
                  onClick={onSwitchToLogin}
                  className="text-primary hover:text-primary/80 p-0"
                >
                  Sign in
                </Button>
              </div>


            </CardContent>
          </Card>
           <MiniFooter/>
        </div>
        
      </div>

      <Toaster />
    </div>
  );
}